import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import { getShowingText, translateGenericStatus } from "../../i18n/dashboardHelpers";
import {
  getPartnershipById,
  getPartnerships,
  PARTNERSHIP_STATUS,
  updatePartnershipStatus,
} from "../../services/apiService";

const PAGE_SIZE = 20;

const tabs = [
  { id: "all", label: "All" },
  { id: PARTNERSHIP_STATUS.PENDING, label: "Pending", status: PARTNERSHIP_STATUS.PENDING },
  { id: PARTNERSHIP_STATUS.APPROVED, label: "Approved", status: PARTNERSHIP_STATUS.APPROVED },
  { id: PARTNERSHIP_STATUS.REJECTED, label: "Rejected", status: PARTNERSHIP_STATUS.REJECTED },
  { id: PARTNERSHIP_STATUS.SUSPENDED, label: "Suspended", status: PARTNERSHIP_STATUS.SUSPENDED },
];

const statusStyles = {
  PENDING: "dashboard-badge--pending",
  APPROVED: "dashboard-badge--active",
  REJECTED: "dashboard-badge--canceled",
  SUSPENDED: "dashboard-badge--inactive",
};

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name = "") {
  const colors = ["#DBEAFE", "#D1FAE5", "#FEF3C7", "#FCE7F3", "#E5E7EB"];
  const index = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
}

function formatPhone(countryCode, phoneNumber) {
  const rawPhone = String(phoneNumber || "").trim();
  const rawCode = String(countryCode || "").trim();

  if (!rawPhone && !rawCode) return "—";
  if (!rawPhone) return rawCode.startsWith("+") ? rawCode : `+${rawCode}`;

  if (rawPhone.startsWith("+") || /[()]/.test(rawPhone)) {
    return rawPhone;
  }

  let code = rawCode;
  if (code && !code.startsWith("+")) {
    code = `+${code}`;
  }

  const phoneDigits = rawPhone.replace(/\D/g, "");
  const codeDigits = code.replace(/\D/g, "");

  if (codeDigits && phoneDigits.startsWith(codeDigits)) {
    const localNumber = phoneDigits.slice(codeDigits.length);
    return localNumber ? `${code} ${localNumber}` : code;
  }

  return code ? `${code} ${rawPhone}` : rawPhone;
}

function getPartnershipName(partnership) {
  return partnership.full_name || partnership.fullName || "—";
}

function getPartnershipPhone(partnership) {
  return formatPhone(
    partnership.country_code || partnership.countryCode,
    partnership.phone_number || partnership.phoneNumber
  );
}

function StatusBadge({ status, t }) {
  const label = translateGenericStatus(t, status);
  const styleKey = String(status || "").toUpperCase();
  return <span className={`dashboard-badge ${statusStyles[styleKey] || ""}`}>{label}</span>;
}

function ViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ApproveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RejectIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function isPendingStatus(status) {
  return status?.toLowerCase() === PARTNERSHIP_STATUS.PENDING;
}

function PartnershipStatusConfirmModal({ confirmAction, onClose, onConfirm, t }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!confirmAction) {
      setIsSubmitting(false);
      setError("");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [confirmAction, isSubmitting, onClose]);

  if (!confirmAction) return null;

  const { partnership, status } = confirmAction;
  const name = getPartnershipName(partnership);
  const isApprove = status === PARTNERSHIP_STATUS.APPROVED;
  const title = isApprove ? t("dashboard.partnership.approveTitle") : t("dashboard.partnership.rejectTitle");
  const message = isApprove
    ? t("dashboard.partnership.approveMessage", { name })
    : t("dashboard.partnership.rejectMessage", { name });

  const handleConfirm = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      await updatePartnershipStatus(partnership.id, status);
      onConfirm?.();
      onClose();
    } catch (fetchError) {
      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          t("dashboard.partnership.updateFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partnership-status-confirm-title"
      >
        <div className="settings-modal__header">
          <h2 id="partnership-status-confirm-title" className="settings-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">{message}</p>

        {error ? (
          <p className="settings-form-status settings-form-status--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="settings-modal__actions">
          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dashboard.common.cancel")}
          </button>
          <button
            type="button"
            className={`settings-btn ${isApprove ? "settings-btn--primary" : "settings-btn--danger"}`}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("dashboard.common.updating")
              : isApprove
                ? t("dashboard.common.approve")
                : t("dashboard.common.reject")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PartnershipViewModal({ partnershipId, onClose, t, locale }) {
  const [partnership, setPartnership] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!partnershipId) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !loading) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [partnershipId, loading, onClose]);

  useEffect(() => {
    if (!partnershipId) return undefined;

    let cancelled = false;

    const loadPartnership = async () => {
      setLoading(true);
      setError("");
      setPartnership(null);

      try {
        const response = await getPartnershipById(partnershipId);
        if (cancelled) return;
        setPartnership(response?.data ?? null);
      } catch (fetchError) {
        if (cancelled) return;
        setError(fetchError?.response?.data?.message || t("dashboard.partnership.detailsLoadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPartnership();

    return () => {
      cancelled = true;
    };
  }, [partnershipId, t]);

  if (!partnershipId) return null;

  const name = partnership ? getPartnershipName(partnership) : t("dashboard.partnership.application");

  return (
    <div className="settings-modal-overlay" onClick={loading ? undefined : onClose} role="presentation">
      <div
        className="settings-modal partnership-view-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partnership-view-title"
      >
        <div className="partnership-view-modal__header">
          <div className="partnership-view-modal__profile">
            <span
              className="dashboard-table__avatar partnership-view-modal__avatar"
              style={{ backgroundColor: avatarColor(name) }}
              aria-hidden="true"
            >
              {loading ? "…" : getInitials(name)}
            </span>
            <div className="partnership-view-modal__profile-text">
              <div className="partnership-view-modal__profile-top">
                <h2 id="partnership-view-title" className="partnership-view-modal__title">
                  {loading ? t("dashboard.common.loading") : name}
                </h2>
                {!loading && partnership?.status && <StatusBadge status={partnership.status} t={t} />}
              </div>
              <p className="partnership-view-modal__subtitle">
                {loading ? t("dashboard.partnership.fetchingDetails") : partnership?.email || t("dashboard.partnership.application")}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="partnership-view-modal__body">
          {loading ? (
            <div className="partnership-view-modal__loading" aria-live="polite">
              <span className="partnership-view-modal__spinner" aria-hidden="true" />
              {t("dashboard.partnership.loadingDetails")}
            </div>
          ) : error ? (
            <p className="settings-form-status settings-form-status--error" role="alert">
              {error}
            </p>
          ) : partnership ? (
            <>
              <section className="partnership-view-modal__section">
                <h3 className="partnership-view-modal__section-title">{t("dashboard.partnership.contactInformation")}</h3>
                <div className="partnership-view-modal__grid">
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("common.fullName")}</span>
                    <span className="partnership-view-modal__value">{name}</span>
                  </div>
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("dashboard.common.email")}</span>
                    <span className="partnership-view-modal__value">{partnership.email || "—"}</span>
                  </div>
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("dashboard.common.phone")}</span>
                    <span className="partnership-view-modal__value">{getPartnershipPhone(partnership)}</span>
                  </div>
                </div>
              </section>

              <section className="partnership-view-modal__section">
                <h3 className="partnership-view-modal__section-title">{t("dashboard.partnership.businessDetails")}</h3>
                <div className="partnership-view-modal__grid">
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("dashboard.partnership.company")}</span>
                    <span className="partnership-view-modal__value">
                      {partnership.company_name || partnership.companyName || "—"}
                    </span>
                  </div>
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("dashboard.partnership.businessType")}</span>
                    <span className="partnership-view-modal__value">
                      {partnership.business_type || partnership.businessType || "—"}
                    </span>
                  </div>
                  <div className="partnership-view-modal__field">
                    <span className="partnership-view-modal__label">{t("dashboard.common.country")}</span>
                    <span className="partnership-view-modal__value">{partnership.country || "—"}</span>
                  </div>
                </div>
              </section>

              <section className="partnership-view-modal__section">
                <h3 className="partnership-view-modal__section-title">{t("dashboard.common.description")}</h3>
                <div className="partnership-view-modal__description">
                  {partnership.description || t("dashboard.partnership.noDescription")}
                </div>
              </section>

              <section className="partnership-view-modal__section partnership-view-modal__section--meta">
                <div className="partnership-view-modal__meta">
                  <div className="partnership-view-modal__meta-item">
                    <span className="partnership-view-modal__label">{t("dashboard.common.submitted")}</span>
                    <span className="partnership-view-modal__value">
                      {formatDate(partnership.created_at || partnership.createdAt, locale)}
                    </span>
                  </div>
                  {(partnership.updated_at || partnership.updatedAt) &&
                    (partnership.updated_at || partnership.updatedAt) !==
                      (partnership.created_at || partnership.createdAt) && (
                      <div className="partnership-view-modal__meta-item">
                        <span className="partnership-view-modal__label">{t("common.lastUpdated", { date: "" }).replace(": ", "")}</span>
                        <span className="partnership-view-modal__value">
                          {formatDate(partnership.updated_at || partnership.updatedAt, locale)}
                        </span>
                      </div>
                    )}
                </div>
              </section>
            </>
          ) : (
            <p className="partnership-view-modal__empty">{t("dashboard.partnership.notFound")}</p>
          )}
        </div>

        <div className="partnership-view-modal__footer">
          <button type="button" className="settings-btn settings-btn--outline" onClick={onClose}>
            {t("dashboard.common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Partnership() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [partnerships, setPartnerships] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewPartnershipId, setViewPartnershipId] = useState(null);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const activeStatus = tabs.find((tab) => tab.id === activeTab)?.status;

  useEffect(() => {
    let cancelled = false;

    const loadPartnerships = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getPartnerships({
          status: activeStatus,
          page,
          pageSize: PAGE_SIZE,
        });

        if (cancelled) return;

        const items = extractItems(response);
        const total = extractTotal(response, items.length);
        setPartnerships(items);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } catch (fetchError) {
        if (cancelled) return;

        setPartnerships([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(fetchError?.response?.data?.message || t("dashboard.partnership.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPartnerships();

    return () => {
      cancelled = true;
    };
  }, [activeStatus, page, refreshKey, t]);

  const handleStatusUpdated = () => {
    setRefreshKey((current) => current + 1);
  };

  const openStatusConfirm = (partnership, status) => {
    setStatusConfirm({ partnership, status });
  };

  const filteredPartnerships = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return partnerships;

    return partnerships.filter((partnership) => {
      const name = getPartnershipName(partnership).toLowerCase();
      const email = partnership.email?.toLowerCase() ?? "";
      const company = (partnership.company_name || partnership.companyName || "").toLowerCase();
      const businessType = (partnership.business_type || partnership.businessType || "").toLowerCase();
      const country = partnership.country?.toLowerCase() ?? "";
      const description = partnership.description?.toLowerCase() ?? "";
      const phone = getPartnershipPhone(partnership).toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        company.includes(query) ||
        businessType.includes(query) ||
        country.includes(query) ||
        description.includes(query) ||
        phone.includes(query)
      );
    });
  }, [partnerships, search]);

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.partnership.title")}>
        <label className="dashboard-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t("dashboard.partnership.searchPlaceholder")}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t("dashboard.partnership.searchAria")}
          />
        </label>
      </DashboardPageHeader>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar">
          <h2 className="dashboard-card__title dashboard-card__title--inline">{t("dashboard.partnership.title")}</h2>
          <div className="dashboard-tabs" role="tablist" aria-label={t("dashboard.partnership.paginationAria")}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard-tabs__item${activeTab === tab.id ? " dashboard-tabs__item--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.id === "all" ? t("dashboard.common.all") : translateGenericStatus(t, tab.status)}
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("dashboard.partnership.applicant")}</th>
                <th>{t("dashboard.common.email")}</th>
                <th>{t("dashboard.common.phone")}</th>
                <th>{t("dashboard.partnership.company")}</th>
                <th>{t("dashboard.partnership.businessType")}</th>
                <th>{t("dashboard.common.country")}</th>
                <th>{t("dashboard.common.description")}</th>
                <th>{t("dashboard.common.status")}</th>
                <th>{t("dashboard.common.submitted")}</th>
                <th>{t("dashboard.common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10}>{t("dashboard.partnership.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10}>{error}</td>
                </tr>
              ) : filteredPartnerships.length === 0 ? (
                <tr>
                  <td colSpan={10}>{t("dashboard.partnership.noPartnerships")}</td>
                </tr>
              ) : (
                filteredPartnerships.map((partnership) => {
                  const name = getPartnershipName(partnership);
                  return (
                    <tr key={partnership.id}>
                      <td>
                        <div className="dashboard-table__customer">
                          <span
                            className="dashboard-table__avatar"
                            style={{ backgroundColor: avatarColor(name) }}
                            aria-hidden="true"
                          >
                            {getInitials(name)}
                          </span>
                          <span className="dashboard-table__name">{name}</span>
                        </div>
                      </td>
                      <td className="dashboard-table__email">{partnership.email || "—"}</td>
                      <td>{getPartnershipPhone(partnership)}</td>
                      <td>{partnership.company_name || partnership.companyName || "—"}</td>
                      <td>{partnership.business_type || partnership.businessType || "—"}</td>
                      <td>{partnership.country || "—"}</td>
                      <td>{partnership.description || "—"}</td>
                      <td>
                        <StatusBadge status={partnership.status} t={t} />
                      </td>
                      <td className="dashboard-table__date">
                        {formatDate(partnership.created_at || partnership.createdAt, i18n.language)}
                      </td>
                      <td>
                        <div className="dashboard-table__actions">
                          <button
                            type="button"
                            className="dashboard-table__actions-btn"
                            aria-label={`${t("dashboard.common.view")} ${name}`}
                            onClick={() => setViewPartnershipId(partnership.id)}
                          >
                            <ViewIcon />
                          </button>
                          {isPendingStatus(partnership.status) && (
                            <>
                              <button
                                type="button"
                                className="dashboard-table__actions-btn dashboard-table__actions-btn--approve"
                                aria-label={`${t("dashboard.common.approve")} ${name}`}
                                onClick={() => openStatusConfirm(partnership, PARTNERSHIP_STATUS.APPROVED)}
                              >
                                <ApproveIcon />
                              </button>
                              <button
                                type="button"
                                className="dashboard-table__actions-btn dashboard-table__actions-btn--reject"
                                aria-label={`${t("dashboard.common.reject")} ${name}`}
                                onClick={() => openStatusConfirm(partnership, PARTNERSHIP_STATUS.REJECTED)}
                              >
                                <RejectIcon />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "partnerships",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.partnership.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      <PartnershipViewModal
        partnershipId={viewPartnershipId}
        onClose={() => setViewPartnershipId(null)}
        t={t}
        locale={i18n.language}
      />

      <PartnershipStatusConfirmModal
        confirmAction={statusConfirm}
        onClose={() => setStatusConfirm(null)}
        onConfirm={handleStatusUpdated}
        t={t}
      />
    </div>
  );
}

export default Partnership;
